import { useCallback } from 'react';
import { GROUP_COLORS } from '../constants';

export function useGroupActions(saveStateRef, groups, setGroups, categories, setCategories, tasks, setTasks, currentUser, setConfirmDialog, setActiveColorPickerGroupId, theme) {
  const canCreate = currentUser?.role === 'admin' || theme?.editorCanCreate;

  const handleAddGroup = useCallback((categoryId) => {
    if (!canCreate) return;
    const newGroup = { id: `g${Date.now()}`, categoryId, title: 'Novo Grupo', color: GROUP_COLORS[groups.length % GROUP_COLORS.length] };
    const newGroups = [...groups, newGroup];
    setGroups(newGroups);
    saveStateRef.current({ groups: newGroups });
  }, [groups, setGroups, saveStateRef, canCreate]);

  const handleAddCategory = useCallback(() => {
    if (!canCreate) return;
    const newCategory = { id: `cat${Date.now()}`, title: 'Nova Categoria', colorClass: 'text-indigo-400' };
    const newCats = [...categories, newCategory];
    setCategories(newCats);
    saveStateRef.current({ categories: newCats });
  }, [categories, setCategories, saveStateRef, canCreate]);

  const handleDeleteGroup = useCallback((groupId) => {
    if (currentUser?.role !== 'admin') return;
    setConfirmDialog({
      title: 'Excluir Grupo', message: 'Tem certeza que deseja excluir este grupo e todas as suas tarefas? Esta ação não pode ser desfeita.',
      onConfirm: () => {
        const newGroups = groups.filter(g => g.id !== groupId);
        const newTasks = tasks.filter(t => t.groupId !== groupId);
        setGroups(newGroups); setTasks(newTasks);
        saveStateRef.current({ groups: newGroups, tasks: newTasks });
        if (setActiveColorPickerGroupId) setActiveColorPickerGroupId(null);
        setConfirmDialog(null);
      }
    });
  }, [currentUser, groups, setGroups, tasks, setTasks, setConfirmDialog, setActiveColorPickerGroupId, saveStateRef]);

  const handleDeleteCategory = useCallback((categoryId) => {
    if (currentUser?.role !== 'admin') return;
    setConfirmDialog({
      title: 'Excluir Categoria', message: 'Tem certeza que deseja excluir esta categoria inteira, incluindo todos os seus grupos e tarefas?',
      onConfirm: () => {
        const newCategories = categories.filter(c => c.id !== categoryId);
        const groupsToDelete = groups.filter(g => g.categoryId === categoryId).map(g => g.id);
        const newGroups = groups.filter(g => g.categoryId !== categoryId);
        const newTasks = tasks.filter(t => !groupsToDelete.includes(t.groupId));
        setCategories(newCategories); setGroups(newGroups); setTasks(newTasks);
        saveStateRef.current({ categories: newCategories, groups: newGroups, tasks: newTasks });
        setConfirmDialog(null);
      }
    });
  }, [currentUser, categories, setCategories, groups, setGroups, tasks, setTasks, setConfirmDialog, saveStateRef]);

  const updateGroupTitle = useCallback((id, newTitle) => {
    const newGroups = groups.map(g => g.id === id ? { ...g, title: newTitle } : g);
    setGroups(newGroups);
    saveStateRef.current({ groups: newGroups });
  }, [groups, setGroups, saveStateRef]);

  const updateCategoryTitle = useCallback((id, newTitle) => {
    const newCats = categories.map(c => c.id === id ? { ...c, title: newTitle } : c);
    setCategories(newCats);
    saveStateRef.current({ categories: newCats });
  }, [categories, setCategories, saveStateRef]);

  const updateGroupColor = useCallback((id, newColor) => {
    const newGroups = groups.map(g => g.id === id ? { ...g, color: newColor } : g);
    setGroups(newGroups);
    saveStateRef.current({ groups: newGroups });
    if (setActiveColorPickerGroupId) setActiveColorPickerGroupId(null);
  }, [groups, setGroups, setActiveColorPickerGroupId, saveStateRef]);

  return {
    handleAddGroup, handleAddCategory,
    handleDeleteGroup, handleDeleteCategory,
    updateGroupTitle, updateCategoryTitle, updateGroupColor,
  };
}
